import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, Phone, Truck, ShieldCheck, MapPin, Repeat, Check } from "lucide-react";
import { useApp, telLink, waLink, WA_MSG_EN, WA_MSG_AR } from "@/context/AppContext";
import { BRANDS, CATEGORY_KEYS, CATEGORY_LABELS } from "@/i18n";
import TyreSearch from "@/components/TyreSearch";
import QuotationForm from "@/components/QuotationForm";
import { Reveal, SectionHead } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const IMG = {
  hero: "https://images.unsplash.com/photo-1719467292463-9a9a3490d69e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  suv: "https://images.unsplash.com/photo-1765519697953-332ad61aff4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  fleet: "https://images.unsplash.com/photo-1591768793355-74d04bb6608f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  wheel: "https://images.unsplash.com/photo-1780510213278-3678a4176c1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  saudi: "https://images.unsplash.com/photo-1722966885396-1f3dcebdf27f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};
const WHY_ICONS = [Repeat, ShieldCheck, MapPin, Truck];

const Home = () => {
  const { tr, lang } = useApp();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const words = tr.hero.h1.split(" ");
  const wa = waLink(lang === "ar" ? WA_MSG_AR : WA_MSG_EN);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        <motion.div style={{ y: yBg }} className="absolute inset-0 z-0">
          <img src={IMG.hero} alt="Premium performance car tyre in Saudi Arabia" className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
          <div className="absolute inset-0 bg-[#050505]/40" />
        </motion.div>

        <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12 w-full">
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 border border-[#006C35] text-[#00a651] text-xs font-bold uppercase tracking-[0.2em] px-4 py-2 mb-6">
            <span className="h-2 w-2 rounded-full bg-[#006C35]" /> {tr.hero.chip}
          </motion.span>

          <h1 className="font-head text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] max-w-4xl text-white">
            {words.map((w, i) => (
              <span key={i} className="inline-block overflow-hidden align-top me-[0.25em]">
                <motion.span className="inline-block"
                  initial={{ y: "110%" }} animate={{ y: 0 }}
                  transition={{ delay: 0.2 + i * 0.05, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
                  {w}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
            className="text-zinc-300 text-base sm:text-lg max-w-2xl mt-6 leading-relaxed">{tr.hero.sub}</motion.p>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
            className="flex flex-wrap gap-3 mt-8">
            <Link to="/quotation" data-testid="hero-quote-btn"
              className="inline-flex items-center gap-2 bg-[#EAB308] text-[#050505] font-bold px-8 py-4 uppercase tracking-wide hover:-translate-y-1 transition-transform">
              {tr.cta.quote} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="hero-wa-btn"
              className="inline-flex items-center gap-2 border border-zinc-600 text-white font-bold px-8 py-4 uppercase tracking-wide hover:border-[#25D366] hover:text-[#25D366] transition-colors">
              <MessageCircle className="h-4 w-4" /> {tr.cta.whatsapp}
            </a>
            <a href={telLink} data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 border border-zinc-600 text-white font-bold px-8 py-4 uppercase tracking-wide hover:border-[#EAB308] hover:text-[#EAB308] transition-colors">
              <Phone className="h-4 w-4" /> {tr.cta.call}
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} className="mt-12">
            <TyreSearch />
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="relative z-10 border-y border-[#27272a] bg-[#0a0a0a] py-6 overflow-hidden">
        <div className="marquee-track animate-marquee">
          {[...BRANDS, ...BRANDS].map((b, i) => (
            <span key={i} className="font-en-head text-3xl md:text-4xl font-black uppercase text-outline px-8 whitespace-nowrap">{b}</span>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative z-10 py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal><SectionHead kicker={tr.hero.head} title={tr.steps.title} sub={tr.steps.sub} /></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {tr.steps.items.map((it, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="border border-[#27272a] bg-[#121212] p-6 h-full hover:border-[#EAB308] transition-colors group">
                <span className="font-en-head text-5xl font-black text-[#27272a] group-hover:text-[#EAB308] transition-colors">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-head text-lg font-bold text-white mt-4">{it.t}</h3>
                <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{it.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="relative z-10 py-24 bg-[#0a0a0a] border-y border-[#27272a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><SectionHead kicker="Categories" title={tr.categoriesTitle} sub={tr.categoriesSub} /></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {CATEGORY_KEYS.map((k, i) => (
              <Reveal key={k} delay={(i % 5) * 0.05}>
                <Link to="/categories" data-testid={`home-cat-${k}`}
                  className="block border border-[#27272a] bg-[#121212] p-5 hover:border-[#EAB308] transition-colors group h-full">
                  <div className="h-1 w-8 bg-[#EAB308] mb-4 group-hover:w-full transition-all duration-300" />
                  <span className="text-white font-semibold text-sm">{CATEGORY_LABELS[lang][k]}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE + WHEEL IMAGE */}
      <section className="relative z-10 py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="relative overflow-hidden border border-[#27272a]">
            <img src={IMG.wheel} alt="Premium alloy wheel and tyre detail" className="w-full h-[420px] object-cover" />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#050505] to-transparent h-24" />
          </div>
        </Reveal>
        <div>
          <Reveal><SectionHead kicker={tr.whyTitle} title={tr.whyTitle} sub={tr.whySub} /></Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {tr.why.map((w, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="flex gap-3">
                    <div className="h-10 w-10 shrink-0 flex items-center justify-center bg-[#EAB308]/10 border border-[#EAB308]/30">
                      <Icon className="h-5 w-5 text-[#EAB308]" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-sm">{w.t}</h3>
                      <p className="text-zinc-400 text-sm mt-1 leading-relaxed">{w.d}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* BRANDS GRID */}
      <section className="relative z-10 py-24 bg-[#0a0a0a] border-y border-[#27272a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><SectionHead kicker="Brands" title={tr.brandsTitle} sub={tr.brandsSub} center /></Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {BRANDS.map((b, i) => (
              <Reveal key={b} delay={(i % 5) * 0.04}>
                <div className="border border-[#27272a] bg-[#121212] py-6 text-center hover:border-[#EAB308] transition-colors">
                  <span className="font-en-head font-bold text-white text-sm uppercase tracking-wide">{b}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-zinc-500 text-xs text-center mt-8 max-w-3xl mx-auto">{tr.brandsDisclaimer}</p>
        </div>
      </section>

      {/* FLEET */}
      <section className="relative z-10 overflow-hidden">
        <img src={IMG.fleet} alt="Commercial fleet trucks Saudi Arabia" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#050505]/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <span className="text-[#EAB308] text-xs font-bold uppercase tracking-[0.25em]">Fleet & Corporate</span>
            <h2 className="font-head text-3xl sm:text-4xl font-black text-white mt-3">{tr.fleet.title}</h2>
            <p className="text-zinc-300 mt-4 leading-relaxed">{tr.fleet.sub}</p>
            <Link to="/fleet" data-testid="home-fleet-cta"
              className="inline-flex items-center gap-2 bg-[#EAB308] text-[#050505] font-bold px-8 py-4 uppercase tracking-wide mt-6 hover:-translate-y-1 transition-transform">
              {tr.fleet.cta} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </Reveal>
          <div className="space-y-3">
            {tr.fleet.points.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="flex items-center gap-3 bg-[#121212]/80 backdrop-blur border border-[#27272a] px-5 py-4">
                  <Check className="h-5 w-5 text-[#006C35] shrink-0" />
                  <span className="text-white text-sm">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative z-10 py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal><SectionHead kicker="Services" title={tr.servicesTitle} sub={tr.servicesSub} /></Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tr.services.map((sv, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06}>
              <div className="border border-[#27272a] bg-[#121212] p-5 hover:border-[#EAB308] transition-colors h-full">
                <div className="flex items-center gap-3">
                  <span className="h-2 w-2 bg-[#EAB308]" />
                  <h3 className="text-white font-semibold text-sm">{sv.t}</h3>
                </div>
                {sv.n && <p className="text-zinc-500 text-xs mt-2 ms-5">({sv.n})</p>}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* QUOTATION FORM */}
      <section id="quote" className="relative z-10 py-24 bg-[#0a0a0a] border-y border-[#27272a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><SectionHead kicker={tr.cta.getPrice} title={tr.form.title} sub={tr.form.sub} center /></Reveal>
          <Reveal delay={0.1}><QuotationForm /></Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 py-24 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal><SectionHead kicker="FAQ" title={tr.faqTitle} center /></Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="w-full" data-testid="home-faq">
            {tr.faqs.map((fq, i) => (
              <AccordionItem key={i} value={`i${i}`} className="border-[#27272a]">
                <AccordionTrigger className="text-white text-start hover:text-[#EAB308] hover:no-underline">{fq.q}</AccordionTrigger>
                <AccordionContent className="text-zinc-400 leading-relaxed">{fq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      {/* CONTACT / MAP */}
      <section className="relative z-10 py-24 bg-[#0a0a0a] border-t border-[#27272a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><SectionHead kicker={tr.hero.head} title={tr.contactTitle} sub={tr.coverage} /></Reveal>
          <div className="grid lg:grid-cols-2 gap-8">
            <Reveal>
              <div className="space-y-4">
                <a href={telLink} className="flex items-center gap-4 border border-[#27272a] bg-[#121212] p-5 hover:border-[#EAB308] transition-colors">
                  <Phone className="h-6 w-6 text-[#EAB308]" /><div><p className="text-zinc-400 text-xs uppercase">{tr.cta.call}</p><p className="text-white font-bold" dir="ltr">+966 53 263 7224</p></div>
                </a>
                <a href={wa} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 border border-[#27272a] bg-[#121212] p-5 hover:border-[#25D366] transition-colors">
                  <MessageCircle className="h-6 w-6 text-[#25D366]" /><div><p className="text-zinc-400 text-xs uppercase">WhatsApp</p><p className="text-white font-bold" dir="ltr">+966 53 263 7224</p></div>
                </a>
                <div className="flex items-center gap-4 border border-[#27272a] bg-[#121212] p-5">
                  <MapPin className="h-6 w-6 text-[#006C35]" /><div><p className="text-zinc-400 text-xs uppercase">Location</p><p className="text-white font-bold">{tr.hero.head}</p></div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border border-[#27272a] h-full min-h-[300px]">
                <iframe title="Jeddah Map" data-testid="jeddah-map" className="w-full h-full min-h-[300px]" loading="lazy"
                  src="https://www.google.com/maps?q=Jeddah,Saudi+Arabia&output=embed" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
